import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import { Card, Container, Label, TabContent, TabPane, FormFeedback } from 'reactstrap';

import AppInputValidation from 'components/App/InputValidation';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import Breadcrumb from 'components/App/Breadcrumb';
import AppLoading from 'components/App/Loading';
import Button from 'components/App/Button';
import DataTableVoucherCampaign from 'components/App/DataTableVoucherCampaign';
import ModalCreateVoucher from 'components/Modals/ModalCreateVoucher';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import Meta from 'components/App/Meta';
import Pagination from 'components/Pagination';
import images from 'assets/images';

import { VOUCHER_CAMPAIGN_TAB } from 'constants/index';
import ENDPOINT from 'constants/endPoint';
import {
  DISCOUNT_TYPE,
  DISCOUNT_TYPE_OPTIONS,
  VOUCHER_APPLY_TYPE,
  VOUCHER_APPLY_TYPE_OPTIONS,
  VOUCHER_USE_TYPE_OPTIONS,
  isBookeseDiscountUseType,
  isBookesePublicUseType,
} from 'constants/voucherCampaign';
import { getParamsFormObject, checkHadError } from 'utils';
import { getRawCurrencyDigits } from 'utils/currencyFormat';
import cn from 'utils/classnames';
import AddWhiteIcon from 'assets/images/add-white.svg';

import {
  buildVoucherCampaignPayload,
  mapCampaignResponseToForm,
  mapVouchersToTableRows,
} from './buildVoucherCampaignPayload';
import campaignValidationSchema from './voucherCampaignValidation';
import styles from './ManagerVoucherCampaign.module.scss';

const PAGE_TITLE = 'Chiến dịch voucher';

const DEFAULT_LIMIT = 10;

const campaignCountFormatter = new Intl.NumberFormat('vi-VN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const NAV_TAB = [
  { title: 'Tất cả', value: VOUCHER_CAMPAIGN_TAB.ALL },
  // { title: 'Sắp diễn ra', value: VOUCHER_CAMPAIGN_TAB.UPCOMING },
  // { title: 'Đang diễn ra', value: VOUCHER_CAMPAIGN_TAB.ONGOING },
  // { title: 'Đã kết thúc', value: VOUCHER_CAMPAIGN_TAB.ENDED },
];

const EMPTY_CAMPAIGN_FORM = {
  campaignName: '',
  voucherType: '',
  discountType: '',
  discountValue: '',
  maxDiscountAmount: '',
  code: '',
  totalVoucher: '',
  maxUsesPerUser: '',
  applyType: '',
  minBookingAmount: '',
  startAt: null,
  endAt: null,
  voucherExpiredAt: null,
};

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

const breadcrumbList = [
  {
    name: 'Quà tặng',
    isActive: false,
    link: ENDPOINT.MANAGER_GIFT,
  },
  {
    name: PAGE_TITLE,
    isActive: true,
    link: '',
  },
];

function ManagerVoucherCampaign({
  isLoadingCampaignDetail,
  isSubmittingCampaign,
  isAddingVoucherCodes,
  isDeletingVouchers,
  voucherDetail,

  actionGetVoucherDetail,
  actionCreateVoucherCampaign,
  actionUpdateVoucherCampaign,
  actionAddCodeToVoucher,
  actionClearVoucherDetail,
  actionDeleteVouchersFromCampaign,
}) {
  const history = useHistory();
  const { campaignId: routeCampaignId } = useParams();

  const campaignId = routeCampaignId || null;
  const isDetailMode = Boolean(campaignId);

  const searchQueries = useMemo(() => {
    const params = new URLSearchParams(history.location.search);
    return {
      page: params.get('page') || 1,
      q: params.get('q') || '',
      statusType: params.get('statusType') || '',
    };
  }, [history.location.search]);

  const campaignInitialValues = useMemo(() => {
    if (
      isDetailMode &&
      voucherDetail &&
      Object.keys(voucherDetail).length > 0
    ) {
      return mapCampaignResponseToForm(voucherDetail);
    }
    return EMPTY_CAMPAIGN_FORM;
  }, [voucherDetail, isDetailMode]);

  const campaignValidation = useFormik({
    enableReinitialize: true,
    initialValues: campaignInitialValues,
    validationSchema: campaignValidationSchema,
    onSubmit: (values) => {
      if (isDetailMode) {
        const campaignPayload = buildVoucherCampaignPayload(values);
        delete campaignPayload.useType;
        actionUpdateVoucherCampaign({
          values: {
            id: campaignId,
            ...campaignPayload,
          },
          callback: () => {
            actionGetVoucherDetail({
              id: campaignId,
              page: searchQueries.page,
              limit: DEFAULT_LIMIT,
            });
          },
        });
      } else {
        actionCreateVoucherCampaign({
          values: buildVoucherCampaignPayload(values),
          callback: (newCampaign) => {
            const newCampaignId = newCampaign?._id;
            if (newCampaignId) {
              history.replace({
                pathname:
                  ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN_DETAIL(newCampaignId),
                search: history.location.search,
              });
            }
          },
        });
      }
    },
  });

  useEffect(() => {
    if (routeCampaignId) {
      actionGetVoucherDetail({
        id: routeCampaignId,
        page: searchQueries.page,
        limit: DEFAULT_LIMIT,
      });
    }
    return () => {
      actionClearVoucherDetail();
    };
  }, [
    routeCampaignId,
    searchQueries.page,
    actionGetVoucherDetail,
    actionClearVoucherDetail,
  ]);

  const voucherList = useMemo(() => {
    if (!voucherDetail || !voucherDetail.vouchers) return [];
    return mapVouchersToTableRows(voucherDetail.vouchers, voucherDetail);
  }, [voucherDetail]);

  const onChangeCampaignField = useCallback(
    (e) => {
      campaignValidation.setFieldValue(e.name, e.value, true);

      if (e.name === 'voucherType') {
        if (!isBookeseDiscountUseType(e.value)) {
          campaignValidation.setFieldValue('discountType', '', false);
          campaignValidation.setFieldValue('discountValue', '', false);
          campaignValidation.setFieldValue('maxDiscountAmount', '', false);
        }

        if (!isBookesePublicUseType(e.value)) {
          campaignValidation.setFieldValue('code', '', false);
          campaignValidation.setFieldValue('totalVoucher', '', false);
          campaignValidation.setFieldValue('maxUsesPerUser', '', false);
          campaignValidation.setFieldValue('applyType', '', false);
          campaignValidation.setFieldValue('minBookingAmount', '', false);
        } else {
          campaignValidation.setFieldValue('voucherExpiredAt', null, false);
        }
      }

      if (e.name === 'discountType' && e.value !== DISCOUNT_TYPE.PERCENT) {
        campaignValidation.setFieldValue('maxDiscountAmount', '', false);
      }

      if (
        e.name === 'applyType'
        && e.value !== VOUCHER_APPLY_TYPE.MIN_BOOKING_AMOUNT
      ) {
        campaignValidation.setFieldValue('minBookingAmount', '', false);
      }
    },
    [campaignValidation],
  );

  const onChangeCurrencyField = useCallback(
    (e) => {
      const { name, value } = e.target;
      campaignValidation.setFieldValue(
        name,
        getRawCurrencyDigits(value),
        true,
      );
    },
    [campaignValidation],
  );

  const { voucherType, discountType, applyType } = campaignValidation.values;
  const isBookeseDiscountVoucher = isBookeseDiscountUseType(voucherType);
  const isBookesePublicVoucher = isBookesePublicUseType(voucherType);
  const isPercentDiscount = discountType === DISCOUNT_TYPE.PERCENT;
  const isAmountDiscount = discountType === DISCOUNT_TYPE.AMOUNT;
  const isMinBookingApplyType = applyType === VOUCHER_APPLY_TYPE.MIN_BOOKING_AMOUNT;

  const onChangeCampaignDate = useCallback(
    (field) => (value) => {
      campaignValidation.setFieldValue(
        field,
        value ? value.toDate() : null,
        true,
      );
    },
    [campaignValidation],
  );

  const toDatePickerValue = useCallback((value) => {
    if (!value) return undefined;
    return moment(value).isValid() ? dayjs(value) : undefined;
  }, []);

  const { startAt: campaignStartAt, endAt: campaignEndAt } =
    campaignValidation.values;

  const disabledStartDate = useCallback((current) => {
    if (!current) return false;
    return current.valueOf() < dayjs().startOf('day').valueOf();
  }, []);

  const disabledEndDate = useCallback(
    (current) => {
      if (!current) return false;
      const baseDate = campaignStartAt ? dayjs(campaignStartAt) : dayjs();
      return (
        current.valueOf() < baseDate.startOf('minute').valueOf()
      );
    },
    [campaignStartAt],
  );

  const disabledVoucherExpiredDate = useCallback(
    (current) => {
      if (!current) return false;
      let baseDate = dayjs();
      if (campaignEndAt) {
        baseDate = dayjs(campaignEndAt);
      } else if (campaignStartAt) {
        baseDate = dayjs(campaignStartAt);
      }
      return (
        current.valueOf() < baseDate.startOf('minute').valueOf()
      );
    },
    [campaignEndAt, campaignStartAt],
  );

  const onSelectFilterByStatus = useCallback(
    (status) => () => {
      history.push({
        pathname: campaignId
          ? ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN_DETAIL(campaignId)
          : ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN,
        search: getParamsFormObject({
          page: 1,
          q: searchQueries.q,
          statusType: status,
        }),
      });
    },
    [campaignId, history, searchQueries],
  );

  const [isOpenCreateVoucherModal, setIsOpenCreateVoucherModal] =
    useState(false);

  const onCloseCreateVoucherModal = useCallback(() => {
    setIsOpenCreateVoucherModal(false);
  }, []);

  const [isOpenDeleteVoucherModal, setIsOpenDeleteVoucherModal] =
    useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  const onDeleteCampaign = useCallback((voucher) => {
    setSelectedVoucher(voucher);
    setIsOpenDeleteVoucherModal(true);
  }, []);

  const onCloseDeleteVoucherModal = useCallback(() => {
    setIsOpenDeleteVoucherModal(false);
    setSelectedVoucher(null);
  }, []);

  const onConfirmDeleteVoucher = useCallback(() => {
    if (!campaignId || !selectedVoucher?._id) {
      return;
    }

    actionDeleteVouchersFromCampaign({
      values: {
        id: campaignId,
        voucherIds: [selectedVoucher._id],
      },
      callback: () => {
        actionGetVoucherDetail({
          id: campaignId,
          page: searchQueries.page,
          limit: DEFAULT_LIMIT,
        });
        onCloseDeleteVoucherModal();
      },
    });
  }, [
    campaignId,
    selectedVoucher,
    searchQueries.page,
    actionDeleteVouchersFromCampaign,
    actionGetVoucherDetail,
    onCloseDeleteVoucherModal,
  ]);

  const onAddCampaign = useCallback(() => {
    setIsOpenCreateVoucherModal(true);
  }, []);

  const onConfirmCreateVoucher = useCallback(
    (codes) => {
      if (!campaignId || !codes?.length) {
        return;
      }

      actionAddCodeToVoucher({
        values: {
          id: campaignId,
          codes,
        },
        callback: () => {
          actionGetVoucherDetail({
            id: campaignId,
            page: searchQueries.page,
            limit: DEFAULT_LIMIT,
          });
          onCloseCreateVoucherModal();
        },
      });
    },
    [
      campaignId,
      searchQueries.page,
      actionAddCodeToVoucher,
      actionGetVoucherDetail,
      onCloseCreateVoucherModal,
    ],
  );

  const filteredVoucherList = useMemo(() => {
    const keyword = searchQueries.q.trim().toLowerCase();
    const { statusType } = searchQueries;

    return voucherList.filter((item) => {
      const matchStatus = !statusType || item.status === statusType;
      const matchKeyword =
        !keyword || item.code?.toLowerCase().includes(keyword);

      return matchStatus && matchKeyword;
    });
  }, [voucherList, searchQueries]);

  const totalPage = useMemo(
    () => Math.ceil((voucherDetail?.totalVoucher || 0) / DEFAULT_LIMIT),
    [voucherDetail?.totalVoucher],
  );

  const onChangePage = useCallback(
    (selected) => {
      history.push({
        pathname: campaignId
          ? ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN_DETAIL(campaignId)
          : ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN,
        search: getParamsFormObject({
          ...searchQueries,
          page: selected.selected + 1,
        }),
      });
    },
    [campaignId, history, searchQueries],
  );

  // const isSearchBtnDisabled = useMemo(
  //   () => searchValidation.values.q.trim() === '',
  //   [searchValidation.values.q],
  // );

  const campaignCountLabel = useMemo(
    () =>
      `${campaignCountFormatter.format(voucherDetail?.totalVoucher || 0)} voucher`,
    [voucherDetail?.totalVoucher],
  );

  const activeTab = searchQueries.statusType || VOUCHER_CAMPAIGN_TAB.ALL;

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name={PAGE_TITLE} />
      <AppLoading loading={isLoadingCampaignDetail} loaderOnPage />

      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        {/* <Card className="Section">
          <form onSubmit={searchValidation.handleSubmit}>
            <div className={styles.FilterRow}>
              <div className={styles.FilterFields}>
                <div className={styles.FilterSearchCol}>
                  <Label className="Section__Search__Label">
                    Tìm kiếm
                  </Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      name="q"
                      id="q"
                      type="text"
                      placeholder="Tìm kiếm theo Tên hoặc Mã voucher"
                      validation={searchValidation}
                      inputClassName={cn('Section__Search__Input', styles.FilterSearchInput)}
                    />

                    <i className="fas fa-search Section__Search__Icon__Search" />
                  </div>
                </div>
              </div>

              <div className={styles.FilterActionsCol}>
                <Button
                  outline
                  classes="Section__Search__Button__ReType mr-24"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </div>
          </form>
        </Card> */}

        <div className={styles.ContentRow}>
          <Card className={cn('Section', styles.CampaignInfoSection)}>
            <h3 className={styles.CampaignInfoTitle}>Thông tin chiến dịch</h3>

            <form
              className={styles.CampaignInfoForm}
              onSubmit={campaignValidation.handleSubmit}
            >
              <div className={styles.FormField}>
                <Label className={styles.FormFieldLabel} htmlFor="campaignName">
                  Tên chiến dịch
                </Label>

                <AppInputValidation
                  name="campaignName"
                  id="campaignName"
                  type="text"
                  placeholder="Nhập tên chiến dịch"
                  validation={campaignValidation}
                  inputClassName={cn(
                    'Section__Search__Input2',
                    styles.FormFieldInput,
                  )}
                />
              </div>

              <div className={styles.FormField}>
                <Label className={styles.FormFieldLabel} htmlFor="voucherType">
                  Loại voucher
                </Label>

                <AppSelectValidation
                  placeholder="Chọn loại voucher"
                  name="voucherType"
                  keyName="voucherType"
                  options={VOUCHER_USE_TYPE_OPTIONS}
                  validation={campaignValidation}
                  value={campaignValidation.values.voucherType}
                  onChange={onChangeCampaignField}
                  isHiddenSearchable
                  isHiddenClearable
                  selectedClassName={styles.VoucherTypeSelect}
                  isDisabled={isDetailMode}
                />
              </div>

              {isBookeseDiscountVoucher && (
                <>
                  <div className={styles.FormField}>
                    <Label className={styles.FormFieldLabel} htmlFor="discountType">
                      Loại giảm giá
                    </Label>

                    <AppSelectValidation
                      placeholder="Chọn loại giảm giá"
                      name="discountType"
                      keyName="discountType"
                      options={DISCOUNT_TYPE_OPTIONS}
                      validation={campaignValidation}
                      value={campaignValidation.values.discountType}
                      onChange={onChangeCampaignField}
                      isHiddenSearchable
                      isHiddenClearable
                      selectedClassName={styles.VoucherTypeSelect}
                      isDisabled={isDetailMode}
                    />
                  </div>

                  <div className={styles.FormField}>
                    <Label className={styles.FormFieldLabel} htmlFor="discountValue">
                      Giá trị giảm
                      {isPercentDiscount ? ' (%)' : ''}
                    </Label>

                    {isAmountDiscount ? (
                      <AppInputNumberValidation
                        name="discountValue"
                        keyName="discountValue"
                        placeholder="Nhập giá trị giảm"
                        validation={campaignValidation}
                        thousandSeparator="."
                        decimalSeparator=","
                        allowNegative={false}
                        isCustomOnChange
                        onChange={onChangeCurrencyField}
                        inputClassName={cn(
                          'Section__Search__Input2',
                          styles.FormFieldInput,
                        )}
                        formatOptions={{
                          decimalScale: 0,
                        }}
                      />
                    ) : (
                      <AppInputValidation
                        name="discountValue"
                        id="discountValue"
                        type="number"
                        placeholder={
                          isPercentDiscount
                            ? 'Nhập % giảm (tối đa 100)'
                            : 'Nhập giá trị giảm'
                        }
                        validation={campaignValidation}
                        min={0}
                        max={isPercentDiscount ? 100 : undefined}
                        inputClassName={cn(
                          'Section__Search__Input2',
                          styles.FormFieldInput,
                        )}
                      />
                    )}
                  </div>

                  {isPercentDiscount && (
                    <div className={styles.FormField}>
                      <Label
                        className={styles.FormFieldLabel}
                        htmlFor="maxDiscountAmount"
                      >
                        Giảm tối đa
                        {isBookesePublicVoucher ? ' (không bắt buộc)' : ''}
                      </Label>

                      <AppInputNumberValidation
                        name="maxDiscountAmount"
                        keyName="maxDiscountAmount"
                        placeholder="Nhập số tiền giảm tối đa"
                        validation={campaignValidation}
                        thousandSeparator="."
                        decimalSeparator=","
                        allowNegative={false}
                        isCustomOnChange
                        onChange={onChangeCurrencyField}
                        inputClassName={cn(
                          'Section__Search__Input2',
                          styles.FormFieldInput,
                        )}
                        formatOptions={{
                          decimalScale: 0,
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              {isBookesePublicVoucher && (
                <>
                  <div className={styles.FormField}>
                    <Label className={styles.FormFieldLabel} htmlFor="code">
                      Mã voucher
                    </Label>

                    <AppInputValidation
                      name="code"
                      id="code"
                      type="text"
                      placeholder="Nhập mã voucher (3–20 ký tự)"
                      validation={campaignValidation}
                      isDisabled={isDetailMode}
                      maxLength={20}
                      inputClassName={cn(
                        'Section__Search__Input2',
                        styles.FormFieldInput,
                      )}
                    />
                  </div>

                  <div className={styles.FormField}>
                    <Label className={styles.FormFieldLabel} htmlFor="totalVoucher">
                      Tổng số voucher
                    </Label>

                    <AppInputValidation
                      name="totalVoucher"
                      id="totalVoucher"
                      type="number"
                      placeholder="Nhập tổng số voucher"
                      validation={campaignValidation}
                      min={1}
                      inputClassName={cn(
                        'Section__Search__Input2',
                        styles.FormFieldInput,
                      )}
                    />
                  </div>

                  <div className={styles.FormField}>
                    <Label
                      className={styles.FormFieldLabel}
                      htmlFor="maxUsesPerUser"
                    >
                      Giới hạn lượt sử dụng mỗi người
                    </Label>

                    <AppInputValidation
                      name="maxUsesPerUser"
                      id="maxUsesPerUser"
                      type="number"
                      placeholder="Để trống = không giới hạn"
                      validation={campaignValidation}
                      min={1}
                      inputClassName={cn(
                        'Section__Search__Input2',
                        styles.FormFieldInput,
                      )}
                    />
                  </div>

                  <div className={styles.FormField}>
                    <Label className={styles.FormFieldLabel} htmlFor="applyType">
                      Điều kiện áp dụng
                    </Label>

                    <AppSelectValidation
                      placeholder="Chọn điều kiện áp dụng"
                      name="applyType"
                      keyName="applyType"
                      options={VOUCHER_APPLY_TYPE_OPTIONS}
                      validation={campaignValidation}
                      value={campaignValidation.values.applyType}
                      onChange={onChangeCampaignField}
                      isHiddenSearchable
                      isHiddenClearable
                      selectedClassName={styles.VoucherTypeSelect}
                    />
                  </div>

                  {isMinBookingApplyType && (
                    <div className={styles.FormField}>
                      <Label
                        className={styles.FormFieldLabel}
                        htmlFor="minBookingAmount"
                      >
                        Đơn hàng tối thiểu
                      </Label>

                      <AppInputNumberValidation
                        name="minBookingAmount"
                        keyName="minBookingAmount"
                        placeholder="Nhập giá trị đơn hàng tối thiểu"
                        validation={campaignValidation}
                        thousandSeparator="."
                        decimalSeparator=","
                        allowNegative={false}
                        isCustomOnChange
                        onChange={onChangeCurrencyField}
                        inputClassName={cn(
                          'Section__Search__Input2',
                          styles.FormFieldInput,
                        )}
                        formatOptions={{
                          decimalScale: 0,
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              <div className={styles.FormField}>
                <Label className={styles.FormFieldLabel} htmlFor="startAt">
                  Ngày bắt đầu
                </Label>

                <DatePicker
                  id="startAt"
                  className={styles.CampaignDatePicker}
                  showTime={{ format: 'HH:mm' }}
                  format={DATE_TIME_FORMAT}
                  value={toDatePickerValue(campaignValidation.values.startAt)}
                  onChange={onChangeCampaignDate('startAt')}
                  needConfirm={false}
                  inputReadOnly
                  placeholder="Bắt đầu vào"
                  disabledDate={disabledStartDate}
                  status={checkHadError(campaignValidation, 'startAt') ? 'error' : undefined}
                />
                {checkHadError(campaignValidation, 'startAt') && (
                  <FormFeedback type="invalid" className="d-block font-size-12 mt-1">
                    {campaignValidation.errors.startAt}
                  </FormFeedback>
                )}
              </div>

              <div className={styles.FormField}>
                <Label className={styles.FormFieldLabel} htmlFor="endAt">
                  Ngày kết thúc
                  {isBookesePublicVoucher ? ' (không bắt buộc)' : ''}
                </Label>

                <DatePicker
                  id="endAt"
                  className={styles.CampaignDatePicker}
                  showTime={{ format: 'HH:mm' }}
                  format={DATE_TIME_FORMAT}
                  value={toDatePickerValue(campaignValidation.values.endAt)}
                  onChange={onChangeCampaignDate('endAt')}
                  needConfirm={false}
                  inputReadOnly
                  placeholder="Kết thúc vào"
                  disabledDate={disabledEndDate}
                  defaultPickerValue={toDatePickerValue(campaignValidation.values.startAt)}
                  status={checkHadError(campaignValidation, 'endAt') ? 'error' : undefined}
                />
                {checkHadError(campaignValidation, 'endAt') && (
                  <FormFeedback type="invalid" className="d-block font-size-12 mt-1">
                    {campaignValidation.errors.endAt}
                  </FormFeedback>
                )}
              </div>

              {!isBookesePublicVoucher && (
                <div className={styles.FormField}>
                  <Label
                    className={styles.FormFieldLabel}
                    htmlFor="voucherExpiredAt"
                  >
                    Ngày hết hạn voucher
                  </Label>

                  <DatePicker
                    id="voucherExpiredAt"
                    className={styles.CampaignDatePicker}
                    showTime={{ format: 'HH:mm' }}
                    format={DATE_TIME_FORMAT}
                    value={toDatePickerValue(
                      campaignValidation.values.voucherExpiredAt,
                    )}
                    onChange={onChangeCampaignDate('voucherExpiredAt')}
                    needConfirm={false}
                    inputReadOnly
                    placeholder="Hết hạn vào"
                    disabledDate={disabledVoucherExpiredDate}
                    defaultPickerValue={
                      toDatePickerValue(campaignValidation.values.endAt) ||
                      toDatePickerValue(campaignValidation.values.startAt)
                    }
                  />
                </div>
              )}

              <div className={styles.CampaignFormActions}>
                <Button
                  type="submit"
                  classes={styles.CampaignConfirmButton}
                  disabled={isSubmittingCampaign || isLoadingCampaignDetail}
                  loading={isSubmittingCampaign}
                >
                  Xác nhận
                </Button>
              </div>
            </form>
          </Card>
          <div className={cn(' ', styles.CampaignListSection)}>
            <Card className={cn('Section')}>
              <div className="Tab__Header__Container">
                <div className="Tab__Header">
                  <nav
                    className="Tab__Header__Nav"
                    aria-label="Lọc trạng thái chiến dịch voucher"
                  >
                    {NAV_TAB.map((tab) => (
                      <div key={tab.value} className="Tab__Header__Tab">
                        <button
                          type="button"
                          onClick={onSelectFilterByStatus(tab.value)}
                          className={`Tab__Header__Tab__Item ${
                            (searchQueries.statusType === tab.value ||
                              (tab.value === VOUCHER_CAMPAIGN_TAB.ALL &&
                                !searchQueries.statusType)) &&
                            'Active'
                          }`}
                        >
                          {tab.title}
                        </button>
                      </div>
                    ))}
                  </nav>
                </div>

                <Button
                  type="button"
                  classes="Tab__Header__Button"
                  onClick={onAddCampaign}
                  disabled={!isDetailMode || isBookesePublicVoucher}
                >
                  <img src={AddWhiteIcon} alt="Add icon" />
                  <span className="Tab__Header__Button__Text">Tạo voucher</span>
                </Button>
              </div>

              <TabContent activeTab={activeTab} className="Table__Body">
                <Label className="Table__Body__Label">
                  {campaignCountLabel}
                </Label>

                {NAV_TAB.map((item) => (
                  <TabPane tabId={item.value} key={item.value}>
                    <div className={styles.CampaignListTableScroll}>
                      <DataTableVoucherCampaign
                        campaigns={filteredVoucherList}
                        onDeleteCampaign={onDeleteCampaign}
                      />
                    </div>
                  </TabPane>
                ))}
              </TabContent>
            </Card>
            {totalPage > 1 && (
              <Pagination
                page={Number(searchQueries.page)}
                pageCount={totalPage}
                onChangePage={onChangePage}
              />
            )}
          </div>
        </div>
      </Container>

      <ModalCreateVoucher
        isOpen={isOpenCreateVoucherModal}
        onCloseModal={onCloseCreateVoucherModal}
        onConfirm={onConfirmCreateVoucher}
        isLoadingConfirm={isAddingVoucherCodes}
      />

      <ModalRequestAction
        isOpen={isOpenDeleteVoucherModal}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xóa \n voucher này không?'}
        onCloseModal={onCloseDeleteVoucherModal}
        onConfirm={onConfirmDeleteVoucher}
        isLoadingConfirm={isDeletingVouchers}
      />
    </div>
  );
}

ManagerVoucherCampaign.propTypes = {
  isLoadingCampaignDetail: PropTypes.bool,
  isSubmittingCampaign: PropTypes.bool,
  isAddingVoucherCodes: PropTypes.bool,
  isDeletingVouchers: PropTypes.bool,
  voucherDetail: PropTypes.instanceOf(Object),

  actionGetVoucherDetail: PropTypes.func.isRequired,
  actionCreateVoucherCampaign: PropTypes.func.isRequired,
  actionUpdateVoucherCampaign: PropTypes.func.isRequired,
  actionAddCodeToVoucher: PropTypes.func.isRequired,
  actionClearVoucherDetail: PropTypes.func.isRequired,
  actionDeleteVouchersFromCampaign: PropTypes.func.isRequired,
};

ManagerVoucherCampaign.defaultProps = {
  isLoadingCampaignDetail: false,
  isSubmittingCampaign: false,
  isAddingVoucherCodes: false,
  isDeletingVouchers: false,
  voucherDetail: {},
};

export default ManagerVoucherCampaign;
